import { Badge } from "flowbite-react";
import {
  PURCHASE_POINTS_UNIT,
  REWARD_EXPIRED_PREFIX,
  REWARD_HISTORY_UNKNOWN_BRAND,
  REWARD_STATUS_LABELS,
  REWARD_VALIDITY_PREFIX,
} from "@/features/purchasePoints/config/purchasePoints.config";
import type {
  PurchasePointsReward,
  PurchasePointsRewardStatus,
} from "@/features/purchasePoints/types/purchasePointsReward.type";

type RewardHistoryRowProps = { reward: PurchasePointsReward };

const BADGE_COLORS: Record<PurchasePointsRewardStatus, string> = {
  pending: "warning",
  acquired: "success",
  cancelled: "gray",
};

// The payload is not validated: a status filed by the provider after this
// release must stay readable rather than render an empty badge.
const UNKNOWN_STATUS_COLOR = "gray";

const LOGO_SIZE = 40;

const SECONDARY_LINE = "text-sm text-gray-500 dark:text-gray-400";
const POINTS = "ml-auto whitespace-nowrap font-semibold";
const LIVE_POINTS = `${POINTS} text-gray-900 dark:text-white`;
const EXPIRED_POINTS = `${POINTS} text-gray-400 dark:text-gray-500`;

const formatDate = (isoDate: string | null) => {
  if (!isoDate) return "";

  const occurredAt = new Date(isoDate);
  return Number.isNaN(occurredAt.getTime()) ? "" : occurredAt.toLocaleDateString("fr-FR");
};

export const RewardHistoryRow = ({ reward }: RewardHistoryRowProps) => (
  <li
    className="flex items-center gap-3 border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700"
    data-testid="reward-history-row"
  >
    {reward.brand.logo_url && (
      <img
        src={reward.brand.logo_url}
        alt=""
        width={LOGO_SIZE}
        height={LOGO_SIZE}
        loading="lazy"
        decoding="async"
        // The host belongs to the enseigne: it has no business knowing which
        // page of the employee's account linked to it.
        referrerPolicy="no-referrer"
        className="size-10 max-w-full rounded object-contain"
        data-testid="reward-history-logo"
      />
    )}

    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium text-gray-900 dark:text-white">
        {reward.brand.name || REWARD_HISTORY_UNKNOWN_BRAND}
      </span>
      <span className={SECONDARY_LINE}>
        {formatDate(reward.occurred_at)}
      </span>

      {/* Whether the points have expired is the server's answer, never a
          comparison of this date against the browser clock. */}
      {reward.points_expire_on && (
        <span className={SECONDARY_LINE} data-testid="reward-history-validity">
          {reward.points_expired ? REWARD_EXPIRED_PREFIX : REWARD_VALIDITY_PREFIX}
          {" "}
          {formatDate(reward.points_expire_on)}
        </span>
      )}
    </div>

    <span className={reward.points_expired ? EXPIRED_POINTS : LIVE_POINTS}>
      {(reward.points ?? 0).toLocaleString("fr-FR")}
      {" "}
      {PURCHASE_POINTS_UNIT}
    </span>

    <Badge color={BADGE_COLORS[reward.status] ?? UNKNOWN_STATUS_COLOR} data-testid="reward-history-status">
      {REWARD_STATUS_LABELS[reward.status] ?? reward.status}
    </Badge>
  </li>
);

RewardHistoryRow.displayName = "RewardHistoryRow";
